import { useEffect, useMemo } from "react";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";

import type { InboxFuryWebPort } from "~/api/ports";
import { AppShell } from "~/components/layout/app-shell";
import { AppSidebar } from "~/components/layout/app-sidebar";
import { AppTopBar } from "~/components/layout/app-top-bar";
import { CommandPalette } from "~/components/layout/command-palette";
import { Button } from "~/components/ui/button";
import { Toaster } from "~/components/ui/toaster";
import { QueryClient } from "@tanstack/react-query";
import { CollectionsProvider } from "~/collections/app-db-context";
import { createAppCollections } from "~/collections/create-app-collections";
import { ClientProvider } from "./client-context";
import { NavigateProvider, type NavigateFn } from "./navigate-context";
import { RouteBoundary } from "./route-boundary";
import { ROUTES, SIGN_IN_PATH, isPublicRoute, matchRoute } from "./routes";
import { SessionGateNotice, useSessionResolution } from "./session-gate";

export interface InboxRouterContext {
  client: InboxFuryWebPort;
  dataMode: "server" | "demo";
  queryClient: QueryClient;
}

function toTanstackPath(path: string): string {
  if (path === "/") return "/";
  return path.replace(/:([A-Za-z0-9_]+)/gu, "$$$1");
}

function AppRoot() {
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { client, dataMode, queryClient } = router.options.context;
  const { resolution, retry } = useSessionResolution(client);
  const match = matchRoute(pathname);
  const publicRoute = match ? isPublicRoute(match.route) : false;
  const sessionStatus = resolution.status;
  const collections = useMemo(() => {
    if (sessionStatus !== "authenticated") return null;
    return createAppCollections(queryClient, client);
  }, [sessionStatus, queryClient, client]);

  const navigate = useMemo<NavigateFn>(() => {
    return (to, options) => {
      if (options?.replace) router.history.replace(to);
      else router.history.push(to);
    };
  }, [router]);

  useEffect(() => {
    if (sessionStatus === "anonymous") {
      document.title = "Sign in · InboxFury";
      return;
    }
    document.title = match ? `${match.route.title} · InboxFury` : "InboxFury";
  }, [match, sessionStatus]);

  useEffect(() => {
    if (publicRoute || sessionStatus !== "anonymous") return;
    if (pathname === SIGN_IN_PATH) return;
    router.history.replace(SIGN_IN_PATH);
  }, [publicRoute, sessionStatus, pathname, router]);

  // The only public document is `/signin`. Render it from the table, not
  // `<Outlet />`: after a 401 replace, TanStack can still be matched on the
  // gated child for a frame, and that child's Suspense fallback is the
  // "Loading" skeleton the API-keys gate test caught.
  if ((match && publicRoute) || sessionStatus === "anonymous") {
    const signIn = ROUTES.find((route) => route.path === SIGN_IN_PATH);
    return (
      <ClientProvider client={client}>
        <NavigateProvider navigate={navigate}>
          <RouteBoundary routeTitle="Sign in" onGoBack={() => router.history.back()}>
            {signIn ? signIn.element() : null}
          </RouteBoundary>
        </NavigateProvider>
      </ClientProvider>
    );
  }

  if (resolution.status !== "authenticated") {
    return <SessionGateNotice resolution={resolution} onRetry={retry} />;
  }

  const flush = match?.route.area === "mail";
  const body = (
      <NavigateProvider navigate={navigate}>
        <a
          href="#main-content"
          className="absolute z-50 m-3 -translate-y-[200%] rounded-md bg-background px-3 py-2 shadow-md focus:translate-y-0 focus:outline-none focus:shadow-focus"
        >
          Skip to content
        </a>
        <CommandPalette navigate={navigate} />
        <AppShell
          flush={flush}
          /*
           * The data-mode indicator lives in the top bar as well as the
           * sidebar header, because the header version disappears below the
           * mobile breakpoint and whenever the sidebar is icon-collapsed — and
           * a demonstration build that silently stops saying it is one is a
           * build claiming an estate it does not have.
           */
          topBar={
            <AppTopBar
              left={
                dataMode === "demo" ? (
                  <span className="rounded-sm bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning-ink">
                    Preview data
                  </span>
                ) : undefined
              }
            />
          }
          sidebar={
            <AppSidebar
              pathname={pathname}
              role={resolution.session.role}
              userId={resolution.session.userId}
              identitySubject={resolution.session.identitySubject}
              dataMode={dataMode}
            />
          }
        >
          {match ? (
            <RouteBoundary
              key={match.route.area === "mail" ? "mail" : pathname}
              routeTitle={match.route.title}
              onGoBack={() => router.history.back()}
            >
              <Outlet />
            </RouteBoundary>
          ) : (
            <div className="mx-auto min-w-0 max-w-md rounded-lg border border-border bg-surface-1 p-8 text-center">
              <h1 className="text-lg font-semibold text-foreground">No such page</h1>
              <p className="mt-2 text-sm text-foreground-muted">
                Nothing is routed at{" "}
                <code className="break-all font-mono text-xs">{pathname}</code>.
              </p>
              <Button
                type="button"
                className="mt-4 focus-visible:ring-0 focus-visible:shadow-focus"
                onClick={() => navigate("/mail")}
              >
                Go to Inbox
              </Button>
            </div>
          )}
        </AppShell>
        <Toaster />
      </NavigateProvider>
  );

  return (
    <ClientProvider client={client}>
      {collections ? <CollectionsProvider collections={collections}>{body}</CollectionsProvider> : body}
    </ClientProvider>
  );
}

const rootRoute = createRootRouteWithContext<InboxRouterContext>()({
  component: AppRoot,
});

const childRoutes = ROUTES.map((route) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: toTanstackPath(route.path),
    component: function RoutedScreen() {
      return <>{route.element()}</>;
    },
  }),
);

const routeTree = rootRoute.addChildren(childRoutes);

export function createInboxRouter(context: InboxRouterContext) {
  return createRouter({
    routeTree,
    context,
    trailingSlash: "never",
    defaultNotFoundComponent: function NotFound() {
      return null;
    },
  });
}

export function InboxRouterProvider({ router }: { router: ReturnType<typeof createInboxRouter> }) {
  return <RouterProvider router={router} />;
}
